import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../Icon';
import { Loader } from '../Loader';
import { MemorySymbolV1 } from '../MemorySymbolV1';
import { PaywallFaqV1 } from '../PaywallFaqV1';
import { SubscriptionPlanSelectorV12 } from '../SubscriptionPlanSelectorV12';
import { CheckoutV5 } from '../CheckoutV5';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { FP_PROMO_MAIN_TYPE } from '../../../lib/shared/promotion-type';
import { getPlanDiscountPercent } from '../../shared/plan-helpers';
import { BLOCKS } from '../../shared/memory-map-data';
import { SIGNS, SIGN_VALUES, getChosenChapter, getMemoryMap, getUserName } from '../../shared/memory-map-helpers';
import {
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN,
    MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN,
    MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN
} from '../../../lib/shared/subscription-plans';
import styles from './SaleV40.css';

const plansWithoutPromo = [MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN, MK_TWELVE_WEEKS_5999_WITHOUT_TRIAL_PLAN, MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN];
const plansWithMainPromo = [MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN, MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN];
const popularPlans = [MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_FOUR_WEEKS_2999_WITHOUT_TRIAL_PLAN];

const HIGHLIGHTS = [
    ['Let go of what you’ve carried', 'Years of it finally have a place to go.'],
    ['Keep the good days close', 'Home, school, first love: written down before the details slip away.'],
    ['Meet yourself again', 'The kid at home, the one at school, the one who loved for the first time. One story, in order.'],
    ['Read it back with pride', 'Turn the pages and think: that was me.'],
    ['Stay in control', 'Talk or type. Edit anything. Share it with the people you pick, or nobody.']
];

const SYMBOL_SIZE = 64;

function SaleV40({
    data,
    initialPrelander,
    expiryTimestamp,
    isExpired = true,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData,
    legalLinks
}) {
    const { plans, isLoading } = useSubscriptionPlans([], isExpired);
    const [isPending, setIsPending] = useState(false);
    const [activePlanData, setActivePlanData] = useState({});
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId);
    const map = getMemoryMap(data.userResponses);
    const name = getUserName(data.userResponses);
    const hasPromo = Boolean(expiryTimestamp) && !isExpired;

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED, { chapter: BLOCKS[getChosenChapter(data.userResponses, map)].short });

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => {
        const planOrder = !isExpired && data.promoType === FP_PROMO_MAIN_TYPE ? plansWithMainPromo : plansWithoutPromo;

        return planOrder
            .map(plan => plans.find(item => item.plan === plan))
            .filter(Boolean)
            .map(plan => ({
                ...plan,
                isPopular: popularPlans.includes(plan.plan)
            }));
    };

    useEffect(() => {
        const newPlans = getPlans();
        const defaultPlan = newPlans.find(plan => plan.isPopular) ?? newPlans[0];

        if (defaultPlan) {
            setActivePlanData(defaultPlan);
        }
    }, [plans, isExpired]);

    const getMetadataForSubscription = () => getMetadata(window.location.search, initialPrelander, data.userResponses);

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: getMetadataForSubscription()
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);

            if (newMerchantData?.fbEventId) {
                logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE, { eventId: newMerchantData.fbEventId });
            }
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: getMetadataForSubscription()
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const handleSubscribe = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan, letters: false });
        setMerchantData({});
        selectSubscription(subscriptionData);
        submitSubscription(subscriptionData);
    };

    if (merchantData.merchant && selectedSubscriptionData.plan) {
        return (
            <div className={styles.fixedModal}>
                <CheckoutV5
                    merchantData={merchantData}
                    subscriptionData={selectedSubscriptionData}
                    promoType={isExpired ? '' : data.promoType}
                    onClose={handleClose}
                    tryAgain={tryAgain}
                    isPending={isPending}
                    successUrl={firstUpsellUrl}
                    termsOfUseLink={legalLinks.termsOfUse}
                    defaultDiscountValue={getPlanDiscountPercent(selectedSubscriptionData)}
                    isWalletEnabled
                />
            </div>
        );
    }

    const renderPlans = hasBoldPrices => (
        <div className={styles.plansBlock}>
            {isLoading
                ? <Loader className={styles.loader} variant='secondary' />
                : (
                    <SubscriptionPlanSelectorV12
                        onSubmit={handleSubscribe}
                        selectedSubscriptionData={activePlanData}
                        selectSubscription={setActivePlanData}
                        isPending={isPending}
                        plans={getPlans()}
                        hasPromo={hasPromo}
                        discountTitle='Your welcome price is applied!'
                        termsOfUseLink={legalLinks.termsOfUse}
                        hasBoldPrices={hasBoldPrices}
                    />
                )
            }
        </div>
    );

    return (
        <div className={styles.root}>
            <section className={styles.section} id='subscription-start-block'>
                <h2 className={styles.title}>
                    {Boolean(name) && <span className={styles.name}>{`${name}, `}</span>}
                    {`${map.total} memories came back in 3 minutes. Let’s keep them.`}
                </h2>
                <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className='fp-block' />
                {renderPlans(false)}
            </section>
            <section className={styles.highlights}>
                <h2 className={styles.title}>What changes for you</h2>
                <ul className={styles.highlightsList}>
                    {HIGHLIGHTS.map(([title, description]) => (
                        <li className={styles.highlight} key={title}>
                            <Icon name='checkItemFilled' className={styles.highlightIcon} />
                            <span className={styles.highlightText}>
                                <b className={styles.highlightTitle}>{title}</b>
                                <span className={styles.highlightDescription}>{description}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </section>
            <section className={styles.ready}>
                <div className={styles.readyText}>
                    <h2 className={styles.readyTitle}>Your map already opened three chapters</h2>
                    <p className={styles.readyNote}>Home, School and First love are outlined. They only need your voice.</p>
                </div>
                <ul className={styles.tiles}>
                    {BLOCKS.map((block, index) => (
                        <li className={styles.tile} key={block.key}>
                            <span className={styles.tileSymbol}>
                                <MemorySymbolV1
                                    type={SIGNS[index]}
                                    value={SIGN_VALUES[SIGNS[index]][map.ch[index].state - 1]}
                                    size={SYMBOL_SIZE}
                                />
                            </span>
                            <b className={styles.tileName}>{block.short}</b>
                            <span className={styles.tag}>READY</span>
                        </li>
                    ))}
                </ul>
            </section>
            <section className={styles.faq}>
                <h2 className={styles.title}>Questions you’re about to ask</h2>
                <PaywallFaqV1 />
            </section>
            <section className={styles.section}>
                <h2 className={styles.title}>Your map is the outline. Page one is waiting.</h2>
                {renderPlans(true)}
            </section>
        </div>
    );
}

SaleV40.propTypes = {
    data: PropTypes.object.isRequired,
    expiryTimestamp: PropTypes.any,
    isExpired: PropTypes.bool,
    initialPrelander: PropTypes.string.isRequired,
    legalLinks: PropTypes.object.isRequired,
    selectSubscription: PropTypes.func.isRequired,
    selectedSubscriptionData: PropTypes.object.isRequired,
    merchantData: PropTypes.object.isRequired,
    setMerchantData: PropTypes.func.isRequired
};

export {
    SaleV40
};
